export type Language = 'en' | 'uk';

const LANGUAGE_STORAGE_KEY = 'medisnap-language';

/**
 * Detects user's locale and returns appropriate language
 * If locale is 'uk' or 'ua', returns 'uk', otherwise returns 'en'
 */
export const detectUserLocale = (): Language => {
  try {
    const userLocale = navigator.language.toLowerCase();

    // Check if locale starts with 'uk' or 'ua' (handles 'uk', 'uk-UA', 'ua', etc.)
    if (userLocale.startsWith('uk') || userLocale.startsWith('ua')) {
      return 'uk';
    }

    return 'en';
  } catch (error) {
    console.error('Failed to detect user locale:', error);
    return 'en'; // Default to English on error
  }
};

/**
 * Saves language preference to localStorage
 */
export const saveLanguageToStorage = (language: Language): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.error('Failed to save language to localStorage:', error);
  }
};

/**
 * Retrieves language preference from localStorage
 * Returns null if not found or on error
 */
export const getLanguageFromStorage = (): Language | null => {
  try {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);

    if (savedLanguage === 'en' || savedLanguage === 'uk') {
      return savedLanguage;
    }

    return null;
  } catch (error) {
    console.error('Failed to get language from localStorage:', error);
    return null;
  }
};

/**
 * Gets the initial language for the app
 * Priority: localStorage > user locale detection > default to 'en'
 */
export const getInitialLanguage = (): Language => {
  try {
    // First, try to get from localStorage
    const savedLanguage = getLanguageFromStorage();
    if (savedLanguage) {
      return savedLanguage;
    }

    // If not in localStorage, detect user locale
    return detectUserLocale();
  } catch (error) {
    console.error('Failed to get initial language:', error);
    return 'en'; // Default to English on any error
  }
};